'use client';

import { c as _c } from "react/compiler-runtime";
import type * as React from 'react';
import { cn } from '@/lib/utils';
import type { UIMediaItem } from '@/types/ui-media';
export type RenderMediaFn = (media: UIMediaItem, index: number, forceAspectRatio?: boolean) => React.ReactElement | null;
interface MediaGridProperties {
  mediaItems: UIMediaItem[];
  renderSingleMedia: RenderMediaFn;
}
const keyForMedia = (media: UIMediaItem | undefined, index: number) => media?.mediaKey ?? media?.url ?? media?.previewImageUrl ?? `${media?.type ?? 'media'}-${index}`;
const roundingClass = (count: number, index: number) => {
  if (count === 1) return 'rounded-lg';
  if (count === 2) return index === 0 ? 'rounded-l-lg' : 'rounded-r-lg';
  if (count === 3) {
    if (index === 0) return 'rounded-l-lg';
    if (index === 1) return 'rounded-tr-lg';
    return 'rounded-br-lg';
  }
  if (count === 4) {
    return ['rounded-tl-lg', 'rounded-tr-lg', 'rounded-bl-lg', 'rounded-br-lg'][index] ?? '';
  }
  if (index === 0) return 'rounded-l-lg';
  if (index === 1) return 'rounded-tr-lg';
  if (index === 2) return 'rounded-br-lg';
  return '';
};
export function MediaGrid(t0) {
  const $ = _c(72);
  const {
    mediaItems,
    renderSingleMedia
  } = t0;
  const count = mediaItems.length;
  let t1;
  if ($[0] !== mediaItems) {
    t1 = index => keyForMedia(mediaItems[index], index);
    $[0] = mediaItems;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const keyForIndex = t1;
  let t2;
  if ($[2] !== count) {
    t2 = index_0 => roundingClass(count, index_0);
    $[2] = count;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const rounding = t2;
  if (count === 1) {
    const media = mediaItems[0];
    if (!media) {
      return null;
    }
    let t3;
    if ($[4] !== media || $[5] !== renderSingleMedia) {
      t3 = renderSingleMedia(media, 0, false);
      $[4] = media;
      $[5] = renderSingleMedia;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== t3) {
      t4 = <div className="max-w-2xl">{t3}</div>;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  if (count === 2) {
    let t3;
    if ($[9] !== keyForIndex || $[10] !== mediaItems || $[11] !== renderSingleMedia || $[12] !== rounding) {
      let t4;
      if ($[14] !== keyForIndex || $[15] !== renderSingleMedia || $[16] !== rounding) {
        t4 = (media_0, index_1) => <div key={keyForIndex(index_1)} className={cn("relative aspect-[16/9] overflow-hidden", rounding(index_1))}>{renderSingleMedia(media_0, index_1, true)}</div>;
        $[14] = keyForIndex;
        $[15] = renderSingleMedia;
        $[16] = rounding;
        $[17] = t4;
      } else {
        t4 = $[17];
      }
      t3 = mediaItems.map(t4);
      $[9] = keyForIndex;
      $[10] = mediaItems;
      $[11] = renderSingleMedia;
      $[12] = rounding;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    let t4;
    if ($[18] !== t3) {
      t4 = <div className="grid grid-cols-2 gap-[2px] max-w-2xl" data-no-conversation={true}>{t3}</div>;
      $[18] = t3;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    return t4;
  }
  if (count === 3) {
    const first = mediaItems[0];
    const second = mediaItems[1];
    const third = mediaItems[2];
    let t3;
    if ($[20] !== first || $[21] !== keyForIndex || $[22] !== renderSingleMedia || $[23] !== rounding) {
      t3 = first ? <div key={keyForIndex(0)} className={cn("relative row-span-2 overflow-hidden", rounding(0))}>{renderSingleMedia(first, 0, true)}</div> : null;
      $[20] = first;
      $[21] = keyForIndex;
      $[22] = renderSingleMedia;
      $[23] = rounding;
      $[24] = t3;
    } else {
      t3 = $[24];
    }
    let t4;
    if ($[25] !== keyForIndex || $[26] !== renderSingleMedia || $[27] !== rounding || $[28] !== second) {
      t4 = second ? <div key={keyForIndex(1)} className={cn("relative overflow-hidden", rounding(1))}>{renderSingleMedia(second, 1, true)}</div> : null;
      $[25] = keyForIndex;
      $[26] = renderSingleMedia;
      $[27] = rounding;
      $[28] = second;
      $[29] = t4;
    } else {
      t4 = $[29];
    }
    let t5;
    if ($[30] !== keyForIndex || $[31] !== renderSingleMedia || $[32] !== rounding || $[33] !== third) {
      t5 = third ? <div key={keyForIndex(2)} className={cn("relative overflow-hidden", rounding(2))}>{renderSingleMedia(third, 2, true)}</div> : null;
      $[30] = keyForIndex;
      $[31] = renderSingleMedia;
      $[32] = rounding;
      $[33] = third;
      $[34] = t5;
    } else {
      t5 = $[34];
    }
    let t6;
    if ($[35] !== t3 || $[36] !== t4 || $[37] !== t5) {
      t6 = <div className="grid grid-cols-2 grid-rows-2 gap-[2px] max-w-2xl aspect-[16/9]" data-no-conversation={true}>{t3}{t4}{t5}</div>;
      $[35] = t3;
      $[36] = t4;
      $[37] = t5;
      $[38] = t6;
    } else {
      t6 = $[38];
    }
    return t6;
  }
  if (count === 4) {
    let t3;
    if ($[39] !== keyForIndex || $[40] !== mediaItems || $[41] !== renderSingleMedia || $[42] !== rounding) {
      let t4;
      if ($[44] !== keyForIndex || $[45] !== renderSingleMedia || $[46] !== rounding) {
        t4 = (media_1, index_2) => <div key={keyForIndex(index_2)} className={cn("relative overflow-hidden", rounding(index_2))}>{renderSingleMedia(media_1, index_2, true)}</div>;
        $[44] = keyForIndex;
        $[45] = renderSingleMedia;
        $[46] = rounding;
        $[47] = t4;
      } else {
        t4 = $[47];
      }
      t3 = mediaItems.map(t4);
      $[39] = keyForIndex;
      $[40] = mediaItems;
      $[41] = renderSingleMedia;
      $[42] = rounding;
      $[43] = t3;
    } else {
      t3 = $[43];
    }
    let t4;
    if ($[48] !== t3) {
      t4 = <div className="grid grid-cols-2 grid-rows-2 gap-[2px] max-w-2xl aspect-[16/9]" data-no-conversation={true}>{t3}</div>;
      $[48] = t3;
      $[49] = t4;
    } else {
      t4 = $[49];
    }
    return t4;
  }
  let t3;
  if ($[50] !== keyForIndex || $[51] !== mediaItems[0] || $[52] !== renderSingleMedia || $[53] !== rounding) {
    t3 = mediaItems[0] ? <div key={keyForIndex(0)} className={cn("relative aspect-[16/9] overflow-hidden", rounding(0))}>{renderSingleMedia(mediaItems[0], 0, true)}</div> : null;
    $[50] = keyForIndex;
    $[51] = mediaItems[0];
    $[52] = renderSingleMedia;
    $[53] = rounding;
    $[54] = t3;
  } else {
    t3 = $[54];
  }
  let t4;
  if ($[55] !== keyForIndex || $[56] !== mediaItems[1] || $[57] !== renderSingleMedia || $[58] !== rounding) {
    t4 = mediaItems[1] ? <div key={keyForIndex(1)} className={cn("relative aspect-[16/9] overflow-hidden", rounding(1))}>{renderSingleMedia(mediaItems[1], 1, true)}</div> : null;
    $[55] = keyForIndex;
    $[56] = mediaItems[1];
    $[57] = renderSingleMedia;
    $[58] = rounding;
    $[59] = t4;
  } else {
    t4 = $[59];
  }
  let t5;
  if ($[60] !== count || $[61] !== keyForIndex || $[62] !== mediaItems[2] || $[63] !== renderSingleMedia || $[64] !== rounding) {
    t5 = mediaItems[2] ? <div key={keyForIndex(2)} className={cn("relative aspect-[16/9] overflow-hidden", rounding(2))}>{renderSingleMedia(mediaItems[2], 2, true)}{count > 3 && <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/50 text-lg font-semibold text-white">+{count - 3}</div>}</div> : null;
    $[60] = count;
    $[61] = keyForIndex;
    $[62] = mediaItems[2];
    $[63] = renderSingleMedia;
    $[64] = rounding;
    $[65] = t5;
  } else {
    t5 = $[65];
  }
  let t6;
  if ($[66] !== t4 || $[67] !== t5) {
    t6 = <div className="grid grid-rows-2 gap-1">{t4}{t5}</div>;
    $[66] = t4;
    $[67] = t5;
    $[68] = t6;
  } else {
    t6 = $[68];
  }
  let t7;
  if ($[69] !== t3 || $[70] !== t6) {
    t7 = <div className="grid grid-cols-2 gap-[2px] max-w-2xl" data-no-conversation={true}>{t3}{t6}</div>;
    $[69] = t3;
    $[70] = t6;
    $[71] = t7;
  } else {
    t7 = $[71];
  }
  return t7;
}